import { TranslationKeys, translate } from '../../../i18n';
import {
  CameraLiveViewStateDto,
  VenueAgentDto,
} from '../../../models/court-recording/dto/venue-agent.dto';
import { boxStatus, cameraSetupCards, cst } from './camera-setup.utils';
import { vt } from './venue-agent.utils';

/**
 * The camera aim view (docs/epics/camera-aim-live-view.md): a club admin on a
 * ladder, phone in hand, watching one camera's picture refresh about once a
 * second while they nudge the bracket.
 *
 * Same rule as `camera-setup.utils.ts`: every decision the page shows — how
 * old the picture is and what colour that is, whether the box is late, what
 * a renewal answer means, which cameras can be aimed — is a pure function
 * here with a truth table beside it. The hooks own the timers and the page
 * is a renderer.
 *
 * Every time in this file is either the API's (`serverTime`, `startedAt`,
 * `capAt`, `X-Frame-Age-Ms`) or a LOCAL elapsed time measured from when an
 * answer arrived. The phone's wall clock is never compared with the API's:
 * a phone a minute off would otherwise show "frozen" on a live picture.
 */

type CameraAimKey = TranslationKeys extends infer K
  ? K extends `court_recording.camera_aim.${infer Rest}`
    ? Rest
    : never
  : never;

export const aimt = (
  key: CameraAimKey,
  params?: Record<string, string | number>,
): string => translate(`court_recording.camera_aim.${key}` as TranslationKeys, params);

// ---------------------------------------------------------------------------
// Cadence
// ---------------------------------------------------------------------------

/** The API's lease is 45 s; a renewal every 15 s survives two lost ones. */
export const LIVE_VIEW_RENEW_INTERVAL_MS = 15_000;
/**
 * After a renewal that got no answer (a 4G drop), try again sooner than the
 * normal cadence: two misses at 15 s would already spend 30 s of a 45 s lease.
 */
export const LIVE_VIEW_RENEW_RETRY_MS = 5_000;
/** Floor between two renewals, so a clock edge around `capAt` cannot spin. */
export const LIVE_VIEW_MIN_RENEW_DELAY_MS = 1_000;
/** Checked this long after `capAt`, so the cap is over by the time we ask. */
export const LIVE_VIEW_CAP_CHECK_GRACE_MS = 1_000;
/** One frame request per second, never two in flight. */
export const FRAME_POLL_INTERVAL_MS = 1_000;
/**
 * How long a start, a renewal or a stop may go unanswered before the page
 * treats it as a dropped request (`unavailable`). Neither `fetchApi` nor
 * `fetchRaw` has a timeout of its own, and one hung renewal would otherwise
 * block every later one while the 45 s lease lapses under a page that still
 * says the session runs.
 */
export const LIVE_VIEW_REQUEST_TIMEOUT_MS = 10_000;
/** Same for one frame poll: a hung poll would otherwise freeze the picture. */
export const FRAME_POLL_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Picture age
// ---------------------------------------------------------------------------

/** Above this many whole seconds the badge turns amber. */
export const FRAME_AGE_LATE_SECONDS = 5;
/** Above this many whole seconds the badge turns red and says "frozen". */
export const FRAME_AGE_FROZEN_SECONDS = 15;

/**
 * What the last frame response said about the picture's age, and when (on the
 * page's clock) it said it. `ageMs` comes from `X-Frame-Age-Ms`, which the API
 * measures against its own store — so the age stays honest on a phone whose
 * clock is wrong.
 */
export interface FrameAgeAnchor {
  ageMs: number;
  receivedAt: number;
}

/**
 * `X-Frame-Age-Ms` as a number, or `null` when absent or not a non-negative
 * number. Absent is a real case: a cross-origin response only exposes the
 * headers the API lists in `Access-Control-Expose-Headers`.
 */
export function parseFrameAgeHeader(value: string | null | undefined): number | null {
  if (value == null || value.trim() === '') return null;
  const ms = Number(value);
  return Number.isFinite(ms) && ms >= 0 ? ms : null;
}

/**
 * The age to show now: the server's age at the response, plus the time that
 * has passed on this page since. A 304 re-anchors it (the picture is the same,
 * the age still moves), so a box that stops uploading shows a growing age
 * rather than a picture that looks live.
 */
export function frameAgeMs(anchor: FrameAgeAnchor | null, now: number): number | null {
  if (!anchor) return null;
  return anchor.ageMs + Math.max(0, now - anchor.receivedAt);
}

export type FrameAgeTone = 'fresh' | 'late' | 'frozen';

/**
 * Decided on WHOLE seconds — the number the badge prints — so "il y a 5 s"
 * is never amber on one render and neutral on the next.
 */
export function frameAgeTone(ageMs: number): FrameAgeTone {
  const seconds = Math.floor(Math.max(0, ageMs) / 1000);
  if (seconds > FRAME_AGE_FROZEN_SECONDS) return 'frozen';
  if (seconds > FRAME_AGE_LATE_SECONDS) return 'late';
  return 'fresh';
}

function formatAgo(ageMs: number): string {
  const seconds = Math.floor(Math.max(0, ageMs) / 1000);
  if (seconds < 60) return cst('ago_seconds', { count: seconds });
  return cst('ago_minutes', { count: Math.floor(seconds / 60) });
}

/** The badge under the picture; `null` = no age known, show no badge. */
export function describeFrameAge(
  ageMs: number | null,
): { tone: FrameAgeTone; label: string } | null {
  if (ageMs === null || !Number.isFinite(ageMs)) return null;
  const tone = frameAgeTone(ageMs);
  const ago = formatAgo(ageMs);
  return { tone, label: tone === 'frozen' ? aimt('age_frozen', { ago }) : ago };
}

// ---------------------------------------------------------------------------
// API time
// ---------------------------------------------------------------------------

/**
 * An API timestamp moved onto the page's clock: `receivedAt` is when the
 * answer carrying `state.serverTime` arrived, so the offset between the two
 * clocks cancels out. `null` when either timestamp is unreadable.
 */
export function serverToLocal(
  iso: string | undefined,
  state: Pick<CameraLiveViewStateDto, 'serverTime'> | null,
  receivedAt: number | null,
): number | null {
  if (!iso || !state || receivedAt === null) return null;
  const at = Date.parse(iso);
  const serverTime = Date.parse(state.serverTime);
  if (!Number.isFinite(at) || !Number.isFinite(serverTime)) return null;
  return receivedAt + (at - serverTime);
}

/**
 * When to renew next: the normal cadence, or just after the 15-minute cap if
 * that comes first — so "Session terminée" shows at the cap instead of up to
 * 15 s of "Image figée" after it.
 */
export function nextRenewalDelayMs(
  state: CameraLiveViewStateDto | null,
  receivedAt: number | null,
  now: number,
): number {
  const capLocal = serverToLocal(state?.capAt, state, receivedAt);
  if (capLocal === null) return LIVE_VIEW_RENEW_INTERVAL_MS;
  const untilCheck = capLocal + LIVE_VIEW_CAP_CHECK_GRACE_MS - now;
  return Math.max(
    LIVE_VIEW_MIN_RENEW_DELAY_MS,
    Math.min(LIVE_VIEW_RENEW_INTERVAL_MS, untilCheck),
  );
}

// ---------------------------------------------------------------------------
// The session
// ---------------------------------------------------------------------------

/** Where the page's session stands. Set by `useCameraLiveViewSession`. */
export type LiveViewPhase = 'idle' | 'opening' | 'active' | 'ended' | 'replaced' | 'error';

/** `AA:BB:CC:DD:EE:FF`, uppercase: the only MAC form the API accepts. */
const API_MAC_RE = /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/;

/**
 * The camera a `?camera=` link names, in the form the API takes — trimmed,
 * uppercased `AA:BB:CC:DD:EE:FF`, as `normalizeLiveViewMac` in
 * `api/src/court-recording/camera-live-view.ts` does — or `null` when it is
 * not a MAC in that form (the API would refuse it with a 400).
 */
export function parseAimMac(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const mac = raw.trim().toUpperCase();
  return API_MAC_RE.test(mac) ? mac : null;
}

/**
 * A MAC reduced to its hex digits, uppercased: `f0-00-06-20-02-53` and
 * `F0:00:06:20:02:53` both give `F00006200253`. Deliberately looser than the
 * API (which takes only the colon form, see `parseAimMac`): it is only used
 * on this page to tell two cameras apart (`sameMac`) and to print the label's
 * tail (`macTail`), never to build a request.
 */
export function macHexDigits(mac: string | null | undefined): string {
  return (mac ?? '').replace(/[^0-9a-f]/gi, '').toUpperCase();
}

export function sameMac(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = macHexDigits(a);
  return left.length > 0 && left === macHexDigits(b);
}

/** The last four hex digits, as `02:53` — what is printed on the camera's label. */
export function macTail(mac: string | null | undefined): string {
  const hex = macHexDigits(mac);
  if (hex.length < 4) return hex;
  const tail = hex.slice(-4);
  return `${tail.slice(0, 2)}:${tail.slice(2)}`;
}

/**
 * What a renewal answer means for this page.
 *
 * - `renewed`  — our session, still running.
 * - `follow`   — a DIFFERENT session on the SAME camera (someone pressed
 *   "Continuer" after the cap, or opened the page at the same moment). The
 *   picture is the one we are looking at, so the page keeps watching it
 *   rather than claiming another camera took over. It never STARTS a session,
 *   so the 15-minute cap still holds.
 * - `replaced` — a session on another camera: one camera per box, last one wins.
 * - `ended`    — nothing runs (stopped, lease lapsed, or capped).
 */
export type RenewalOutcome = 'renewed' | 'follow' | 'replaced' | 'ended';

export function classifyRenewal(
  own: { sessionId: string; mac: string },
  answer: CameraLiveViewStateDto | null,
): RenewalOutcome {
  if (!answer || !answer.active || !answer.sessionId) return 'ended';
  if (answer.sessionId === own.sessionId) return 'renewed';
  if (sameMac(answer.mac, own.mac)) return 'follow';
  return 'replaced';
}

/**
 * Whether an `active: false` answer came at or after the 15-minute cap of the
 * session we last saw running — only to word the ended state.
 */
export function endedByCap(
  previous: CameraLiveViewStateDto | null,
  answer: CameraLiveViewStateDto | null,
): boolean {
  if (!previous?.capAt || !answer) return false;
  const capAt = Date.parse(previous.capAt);
  const serverTime = Date.parse(answer.serverTime);
  if (!Number.isFinite(capAt) || !Number.isFinite(serverTime)) return false;
  return serverTime >= capAt - LIVE_VIEW_CAP_CHECK_GRACE_MS;
}

/**
 * The box picks a session up on its next heartbeat (up to ~60 s); a box that
 * has not asked for a single upload URL 90 s after the start is most likely
 * running a recorder that predates the aim view.
 */
export const BOX_LATE_AFTER_MS = 90_000;

export function isBoxLate(
  state: CameraLiveViewStateDto | null,
  receivedAt: number | null,
  now: number,
): boolean {
  if (!state?.active || state.lastFrameAt) return false;
  const startedLocal = serverToLocal(state.startedAt, state, receivedAt);
  if (startedLocal === null) return false;
  return now - startedLocal > BOX_LATE_AFTER_MS;
}

export type LiveViewErrorKind = 'invalid' | 'forbidden' | 'not-found' | 'revoked' | 'unavailable';

/**
 * The API's refusals, by status (`ApiError` and `ApiRequestFailure` both
 * carry one; read structurally so this file stays import-free of the client).
 * Anything without a 4xx we know — a dropped connection, a 5xx — is
 * `unavailable`, the one kind worth retrying silently.
 */
export function liveViewErrorKind(error: unknown): LiveViewErrorKind {
  const status = (error as { status?: unknown } | null)?.status;
  switch (status) {
    case 400:
      return 'invalid';
    case 403:
      return 'forbidden';
    case 404:
      return 'not-found';
    case 409:
      return 'revoked';
    default:
      return 'unavailable';
  }
}

export function describeLiveViewError(kind: LiveViewErrorKind): string {
  switch (kind) {
    case 'invalid':
      return aimt('error_invalid');
    case 'forbidden':
      return aimt('error_forbidden');
    case 'not-found':
      return aimt('error_not_found');
    case 'revoked':
      return aimt('error_revoked');
    case 'unavailable':
      return aimt('error_unavailable');
  }
}

/** What the picture area shows. */
export type AimViewDisplay =
  | { kind: 'opening' }
  | { kind: 'starting' }
  | { kind: 'box-late' }
  | { kind: 'live' }
  | { kind: 'blocked'; blocker: AimBlocker }
  | { kind: 'ended'; capped: boolean }
  | { kind: 'replaced' }
  | { kind: 'error'; error: LiveViewErrorKind };

/**
 * A picture on screen wins over every "waiting" state: `lastFrameAt` only
 * reaches the page with the next renewal (≤ 15 s), the frame relay is polled
 * every second. Without a picture, a known reason the camera cannot stream
 * (box offline, camera unplugged) beats "starting" and "box needs an update",
 * both of which would send the admin looking in the wrong place.
 */
export function aimViewDisplay(input: {
  phase: LiveViewPhase;
  state: CameraLiveViewStateDto | null;
  receivedAt: number | null;
  hasFrame: boolean;
  endedByCap: boolean;
  error: unknown;
  /** `aimBlocker` for the camera on screen. */
  blocker: AimBlocker | null;
  now: number;
}): AimViewDisplay {
  switch (input.phase) {
    case 'error':
      return { kind: 'error', error: liveViewErrorKind(input.error) };
    case 'replaced':
      return { kind: 'replaced' };
    case 'ended':
      return { kind: 'ended', capped: input.endedByCap };
    case 'active':
      if (input.hasFrame) return { kind: 'live' };
      if (input.blocker) return { kind: 'blocked', blocker: input.blocker };
      return isBoxLate(input.state, input.receivedAt, input.now)
        ? { kind: 'box-late' }
        : { kind: 'starting' };
    default:
      return { kind: 'opening' };
  }
}

// ---------------------------------------------------------------------------
// Which cameras can be aimed
// ---------------------------------------------------------------------------

export type AimBlocker = 'revoked' | 'box_offline' | 'no_mac' | 'camera_disconnected';

/**
 * Why a camera cannot be aimed right now, `null` when it can. Same notion of
 * "online" as the Caméra → Terrain panel (`boxStatus`); the API also needs a
 * MAC it has seen in the box's heartbeat, and a camera that is not streaming
 * has no picture to send.
 */
export function aimBlocker(
  agent: VenueAgentDto,
  camera: { mac: string | null; connected: boolean | null },
  now: number = Date.now(),
): AimBlocker | null {
  const boxBlocker = aimBoxBlocker(agent, now);
  if (boxBlocker) return boxBlocker;
  if (!camera.mac) return 'no_mac';
  if (camera.connected !== true) return 'camera_disconnected';
  return null;
}

/** The half of `aimBlocker` that is about the box, not one camera. */
export function aimBoxBlocker(
  agent: VenueAgentDto,
  now: number = Date.now(),
): 'revoked' | 'box_offline' | null {
  const status = boxStatus(agent, now);
  if (status === 'revoked') return 'revoked';
  if (status !== 'online') return 'box_offline';
  return null;
}

export function describeAimBlocker(blocker: AimBlocker): string {
  switch (blocker) {
    case 'revoked':
      return aimt('blocker_revoked');
    case 'box_offline':
      return aimt('blocker_box_offline');
    case 'no_mac':
      return aimt('blocker_no_mac');
    case 'camera_disconnected':
      return aimt('blocker_camera_disconnected');
  }
}

/** The court a camera stands for, named the way the panel names it. */
export function aimCourtLabel(courtId: number | null, nameById: Map<number, string>): string {
  if (courtId === null) return aimt('no_court');
  return nameById.get(courtId) ?? vt('unknown_court', { id: courtId });
}

export interface AimCameraOption {
  mac: string;
  courtLabel: string;
  /** `macTail(mac)`, for telling two cameras on one court apart. */
  tail: string;
  blocker: AimBlocker | null;
}

/**
 * The box's cameras that have a MAC, in the order the box reports them, each
 * named by the court the panel shows for it (the admin assignment, else the
 * box's own config — `cameraSetupCards`). An assignment for a camera the box
 * no longer reports is left out: there is nothing to aim.
 */
export function aimCameraOptions(
  agent: VenueAgentDto,
  nameById: Map<number, string>,
  now: number = Date.now(),
): AimCameraOption[] {
  return cameraSetupCards(agent, {}, now)
    .filter((card) => card.row.mac !== null && card.row.state !== 'not-reporting')
    .map((card) => ({
      mac: card.row.mac as string,
      courtLabel: aimCourtLabel(card.courtId, nameById),
      tail: macTail(card.row.mac),
      blocker: aimBlocker(agent, card.row, now),
    }));
}

/** What the aim page's header says about the camera on screen. */
export interface AimCameraHeader {
  /** The court it films; `null` when unknown — the header then names it by `tail`. */
  courtLabel: string | null;
  tail: string;
  /** Why it cannot stream right now, if known (see `aimViewDisplay`). */
  blocker: AimBlocker | null;
}

/**
 * The header for the camera a valid `?camera=` names. Normally that is its
 * switcher option. But the session never depends on the box reporting the
 * camera at this instant — a box rebooting, or a camera whose MAC is not
 * resolved yet, drops the row for a heartbeat or two, and ending the session
 * then would black the picture out until the admin starts again. Without a
 * row the camera is named by the court an admin assigned it, if any, else by
 * its MAC tail, and only the box-level blockers are known.
 */
export function aimCameraHeader(
  agent: VenueAgentDto,
  mac: string,
  options: AimCameraOption[],
  nameById: Map<number, string>,
  now: number = Date.now(),
): AimCameraHeader {
  const option = options.find((candidate) => sameMac(candidate.mac, mac));
  if (option) return { courtLabel: option.courtLabel, tail: option.tail, blocker: option.blocker };
  const assigned = Object.entries(agent.cameraAssignments ?? {}).find(([assignedMac]) =>
    sameMac(assignedMac, mac),
  );
  return {
    courtLabel: assigned ? aimCourtLabel(assigned[1], nameById) : null,
    tail: macTail(mac),
    blocker: aimBoxBlocker(agent, now),
  };
}

/**
 * `#/admin/court-recording/aim/<agentId>?camera=<MAC>`, colons kept readable
 * (they are legal in a query string) so the link can be read off a QR code.
 */
export function aimPagePath(agentId: string, mac: string): string {
  const camera = encodeURIComponent(mac).replace(/%3A/gi, ':');
  return `/admin/court-recording/aim/${encodeURIComponent(agentId)}?camera=${camera}`;
}

export const VENUE_AGENTS_PATH = '/admin/court-recording/venue-agents';
